import React from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';

import { blackberry, white } from 'lib/css/colors';

const dotBlink = {
  animationName: {
    '0%, 100%': { opacity: 0.2 },
    '50%': { opacity: 1 },
  },
  animationDuration: '0.9s',
  animationIterationCount: 'infinite',
  animationTimingFunction: 'ease-in-out',
};

const styles = StyleSheet.create({
  badge: {
    fontSize: '13px',
    fontWeight: 600,
    color: white,
    backgroundColor: blackberry,
    padding: '5px 14px',
    borderRadius: '12px',
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: '28px',
  },
  loadingRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  loadingLabel: {
    letterSpacing: '0.02em',
  },
  dotRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
  },
  dot: {
    width: 4,
    height: 4,
    borderRadius: '50%',
    backgroundColor: white,
    ...dotBlink,
  },
  dotDelay0: {
    animationDelay: '0s',
  },
  dotDelay1: {
    animationDelay: '0.18s',
  },
  dotDelay2: {
    animationDelay: '0.36s',
  },
});

/**
 * Pill badge: shows a localized count + item label, or a loading state with blinking dots.
 */
function AsyncItemCountBadge({ count, itemsLabel, loading }) {
  return (
    <span
      className={css(styles.badge)}
      role="status"
      aria-live="polite"
      aria-busy={loading}
      aria-label={loading ? 'Loading' : `${count.toLocaleString()} ${itemsLabel}`}
    >
      {loading ? (
        <span className={css(styles.loadingRow)}>
          <span className={css(styles.loadingLabel)}>Loading</span>
          <span className={css(styles.dotRow)} aria-hidden="true">
            <span className={css(styles.dot, styles.dotDelay0)} />
            <span className={css(styles.dot, styles.dotDelay1)} />
            <span className={css(styles.dot, styles.dotDelay2)} />
          </span>
        </span>
      ) : (
        `${count.toLocaleString()} ${itemsLabel}`
      )}
    </span>
  );
}

AsyncItemCountBadge.propTypes = {
  loading: PropTypes.bool.isRequired,
  count: PropTypes.number.isRequired,
  itemsLabel: PropTypes.string,
};

AsyncItemCountBadge.defaultProps = {
  itemsLabel: 'products',
};

export default AsyncItemCountBadge;
